import { format } from 'date-fns';
import classNames from 'classnames';

import type { Event } from '~/types';
import { Markdown } from '~/components/CustomMarkdown';
import { Button } from '~/components/ui/button';
import { Link } from 'react-router';
import { useState, useRef, useEffect } from 'react';
import { ArrowRightIcon, CalendarDays, ChevronRightIcon } from 'lucide-react';

interface Props {
  orgSlug: string;
  eventSlug: string;
  event: Event;
  hasCustomDomain: boolean;
}

export function OrgEvent({
  orgSlug,
  eventSlug,
  event,
  hasCustomDomain,
}: Props) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      setIsOverflowing(contentRef.current.scrollHeight > 120);
    }
  }, [event.description]);

  let dateString = format(new Date(event.startDate), 'MMMM d, yyyy');

  if (event.startDate !== event.endDate) {
    dateString += ` - ${format(new Date(event.endDate), 'MMMM d, yyyy')}`;
  }

  return (
    <div className="flex flex-col gap-2">
      <div>
        <h3 className="text-2xl md:text-3xl font-bold">{event.name}</h3>
        <p className="flex items-center gap-2 text-base md:text-lg text-muted-foreground">
          <CalendarDays size={18} />
          {dateString}
        </p>
      </div>
      <div className="rounded-lg border p-4 pt-4 text-sm">
        <div className="relative">
          <div
            ref={contentRef}
            role={isOverflowing ? 'button' : undefined}
            tabIndex={isOverflowing ? 0 : -1}
            className={classNames(
              'transition-all duration-300 ease-in-out',
              isExpanded
                ? 'max-h-[400px] overflow-y-auto'
                : 'max-h-[120px] overflow-y-hidden',
              isOverflowing && !isExpanded ? 'cursor-pointer' : '',
            )}
            onClick={() => isOverflowing && setIsExpanded(true)}
            onKeyDown={(e) => {
              if (isOverflowing && (e.key === 'Enter' || e.key === ' ')) {
                setIsExpanded(true);
              }
            }}
          >
            <Markdown>{event.description}</Markdown>
          </div>
          {isOverflowing && (
            <div
              className={classNames(
                'pointer-events-none absolute inset-x-0 bottom-0 h-full bg-linear-to-b from-transparent via-transparent via-70% to-background transition-opacity duration-300 ease-in-out',
                isExpanded ? 'opacity-0' : 'opacity-100',
              )}
            />
          )}
        </div>
        {isOverflowing && (
          <div className="flex justify-end">
            <Button
              variant="link"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? 'Read less' : 'Read more'} <ChevronRightIcon />
            </Button>
          </div>
        )}
        <div className="flex w-full">
          {event.closed === 'manual' ? (
            <Button className="w-full" disabled>
              Registration Closed
            </Button>
          ) : (
            <Link
              to={
                hasCustomDomain
                  ? `/event/${eventSlug}`
                  : `/${orgSlug}/event/${eventSlug}`
              }
              className="w-full"
            >
              <Button className="w-full">
                {event.name} Registration <ArrowRightIcon className="h-4 w-4" />
              </Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
